import { format } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'

import { bookingsApi } from '@/api/client'
import type { GetDayAvailabilityWithZoneId200ResponseInner } from '@/openapi'

type AvailabilityZone = GetDayAvailabilityWithZoneId200ResponseInner

export const useReservationStore = defineStore('booking', () => {
  const bookingId = ref<string | null>(null)

  const diners = ref<number | null>(null)
  const dateTime = ref<string | null>(null)
  const zoneId = ref<string | null>(null)
  const zoneName = ref<string | null>(null)

  const name = ref<string | null>(null)
  const surname = ref<string | null>(null)
  const email = ref<string | null>(null)
  const phone = ref<string | null>(null)
  const customerComments = ref<string | null>(null)
  const termsConditionsChecked = ref<boolean>(false)
  const marketingCommunicationChecked = ref<boolean>(false)

  const policy = ref<'none' | 'deposit' | 'card'>('none')
  const policyAmount = ref<number>(0)
  const policyMethod = ref<'all' | 'large-groups' | 'by-person' | null>(null)
  const policyCancellationUntil = ref<string | null>(null)
  const source = ref<string>('LastBooking')

  const availableZones = ref<AvailabilityZone[]>([])
  const conversionToken = ref<string | null>(null)

  function setDiners(value: number | null) {
    diners.value = value
    dateTime.value = null
  }

  function lookupZoneName(id: string | null): string | null {
    if (!id) return null
    return availableZones.value.find(zone => zone.id === id)?.name ?? null
  }

  function setZone(id: string | null, name: string | null = null) {
    zoneId.value = id
    zoneName.value = name ?? lookupZoneName(id)
  }

  function setDateTime(value: string | null) {
    dateTime.value = value
  }

  function setName(value: string | null) {
    name.value = value
  }

  function setSurname(value: string | null) {
    surname.value = value
  }

  function setEmail(value: string | null) {
    email.value = value
  }

  function setPhone(value: string | null) {
    phone.value = value
  }

  function setCustomerComments(value: string | null) {
    customerComments.value = value
  }

  function setTermsConditionsChecked(value: boolean) {
    termsConditionsChecked.value = value
  }

  function setMarketingCommunicationChecked(value: boolean) {
    marketingCommunicationChecked.value = value
  }

  function setBookingId(value: string | null) {
    bookingId.value = value
  }

  function setSource(value: string) {
    source.value = value
  }

  function setAvailableZones(value: AvailabilityZone[]) {
    availableZones.value = value
    if (zoneId.value && !zoneName.value) {
      zoneName.value = lookupZoneName(zoneId.value)
    }
  }

  function setConversionToken(value: string | null) {
    conversionToken.value = value
  }

  async function getBookingInfo() {
    if (!bookingId.value) {
      return
    }

    const response = await bookingsApi.getReservation({
      reservationId: bookingId.value
    })
    setBookingInfo(response)
  }

  function setBookingInfo({
    diners: newDiners,
    dateTime: newDateTime,
    zone: newZoneName,
    zoneId: newZoneId,
    name: newName,
    surname: newSurname,
    email: newEmail,
    phoneNumber,
    customerComments: newCustomerComments,
    policy: newPolicy,
    policyAmount: newPolicyAmount,
    policyMethod: newPolicyMethod,
    policyCancellationUntil: newPolicyCancellationUntil
  }: any) {
    diners.value = newDiners
    zoneId.value = newZoneId ?? null
    zoneName.value = newZoneName ?? null
    dateTime.value = newDateTime
    name.value = newName
    surname.value = newSurname
    email.value = newEmail
    phone.value = phoneNumber
    customerComments.value = newCustomerComments
    termsConditionsChecked.value = true
    policy.value = newPolicy || 'none'
    policyAmount.value = newPolicyAmount || 0
    policyMethod.value = newPolicyMethod || null
    policyCancellationUntil.value = newPolicyCancellationUntil
      ? format(new Date(newPolicyCancellationUntil), 'yyyy-MM-dd HH:mm')
      : null
  }

  function resetState() {
    bookingId.value = null
    diners.value = null
    dateTime.value = null
    name.value = null
    surname.value = null
    email.value = null
    phone.value = null
    customerComments.value = null
    termsConditionsChecked.value = false
    marketingCommunicationChecked.value = false
    zoneId.value = null
    zoneName.value = null
    availableZones.value = []
  }

  return {
    diners,
    dateTime,
    name,
    surname,
    email,
    phone,
    customerComments,
    termsConditionsChecked,
    marketingCommunicationChecked,
    bookingId,
    zoneId,
    zoneName,
    source,
    policy,
    policyAmount,
    policyMethod,
    policyCancellationUntil,
    availableZones,
    conversionToken,

    setDiners,
    setZone,
    setDateTime,
    setName,
    setSurname,
    setEmail,
    setPhone,
    setCustomerComments,
    setTermsConditionsChecked,
    setMarketingCommunicationChecked,
    setBookingId,
    getBookingInfo,
    setBookingInfo,
    setSource,
    setAvailableZones,
    setConversionToken,
    resetState
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReservationStore, import.meta.hot))
}
